<template>  <AppModal
    :show="show"
    @close="closeDialog"
    :title="isEditMode ? $t('projects.editAccess') : $t('projects.inviteClient')"
    :icon="isEditMode ? 'M12,17A2,2 0 0,0 14,15C14,13.89 13.1,13 12,13A2,2 0 0,0 10,15A2,2 0 0,0 12,17M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V10C4,8.89 4.9,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z' : 'M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z'"
    size="large"
    >    <div class="access-invite__content">
      <form @submit.prevent class="access-invite__form">
        <!-- Client Information Section for Edit Mode -->
        <div v-if="isEditMode" class="access-invite__client-info">
          <h3 class="access-invite__section-title">{{ $t('projects.clientInformation') }}</h3>
          <div class="access-invite__info-grid">
            <div class="access-invite__info-item">
              <label>{{ $t('projects.clientName') }}</label>
              <div class="access-invite__info-value">{{ accessData.clientName }}</div>
            </div>
            <div class="access-invite__info-item">
              <label>{{ $t('projects.emailAddress') }}</label>
              <div class="access-invite__info-value">{{ accessData.email }}</div>
            </div>
          </div>
        </div>

        <!-- Client Input Fields for Create Mode -->
        <div v-if="!isEditMode" class="access-invite__form-row">
          <div class="access-invite__form-group">
            <label for="clientName">{{ $t('projects.clientName') }} <span class="access-invite__required">*</span></label>
            <div class="access-invite__input-with-icon">
              <svg viewBox="0 0 24 24" width="18" height="18" class="access-invite__input-icon" aria-hidden="true">
                <path fill="currentColor" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
              </svg>
              <input
                id="clientName"
                v-model="invitationForm.clientName"
                type="text"
                :placeholder="$t('projects.clientNamePlaceholder')"
                ref="firstInput"
                @input="validateOnInput('clientName')"
                :class="{'access-invite__has-error': validationErrors.clientName}"
                required
              />
            </div>
            <div v-if="validationErrors.clientName" class="access-invite__error-message" role="alert">
              {{ validationErrors.clientName }}
            </div>
            <div v-else class="access-invite__form-hint">
              {{ $t('projects.clientNameHint') }}
            </div>
          </div>
          
          <div class="access-invite__form-group">
            <label for="clientEmail">{{ $t('projects.emailAddress') }} <span class="access-invite__required">*</span></label>
            <div class="access-invite__input-with-icon">
              <svg viewBox="0 0 24 24" width="18" height="18" class="access-invite__input-icon" aria-hidden="true">
                <path fill="currentColor" d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
              </svg>
              <input
                id="clientEmail"
                v-model="invitationForm.email"
                type="email"
                :placeholder="$t('projects.emailPlaceholder')"
                @input="validateOnInput('email')"
                :class="{'access-invite__has-error': validationErrors.email}"
                required
              />
            </div>
            <div v-if="validationErrors.email" class="access-invite__error-message" role="alert">
              {{ validationErrors.email }}
            </div>
          </div>
        </div>
          <div class="access-invite__form-group">
          <label for="accessRights">{{ $t('projects.accessRights') }} <span class="access-invite__required">*</span></label>
          <div class="access-invite__access-rights-grid">
            <template v-for="option in accessOptions" :key="option.value">
              <PermissionGuard :permission="getPermissionForOption(option.value)" :projectId="props.projectId">
                <div
                  class="access-invite__access-option"
                  :class="{'access-invite__selected': currentAccessRights.includes(option.value)}"
                  @click="toggleAccessRight(option.value)"
                >
                  <div class="access-invite__access-option-content">
                    <div class="access-invite__access-option-checkbox">
                      <input
                        type="checkbox"
                        :id="'access-' + option.value"
                        :value="option.value"
                        :checked="currentAccessRights.includes(option.value)"
                        @change="toggleAccessRight(option.value)"
                      />
                    </div>
                    <label class="access-invite__access-option-label" :for="'access-' + option.value">
                      <span class="access-invite__access-option-icon" v-if="option.icon">
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <path fill="currentColor" :d="option.icon" />
                        </svg>
                      </span>
                      <span class="access-invite__access-option-title">{{ option.label }}</span>
                    </label>
                  </div>
                </div>
              </PermissionGuard>
            </template>
          </div>
          <div v-if="validationErrors.accessRights" class="access-invite__error-message" role="alert">
            {{ validationErrors.accessRights }}
          </div>        
        </div>

        <!-- Notifications are handled centrally via useNotifications composable -->
      </form>
    </div>    
    <template #footer>
      <div class="access-invite__dialog-actions">
        <AppButton
          variant="secondary"
          @click="closeDialog"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <PermissionGuard :permission="'MANAGE_PROJECT_ACCESS'" :projectId="props.projectId">
          <AppButton
            variant="primary"
            type="button"
            :disabled="isSubmitting"
            :loading="isSubmitting"
            @click="handleClick"
          >
            {{ isSubmitting ? (isEditMode ? $t('common.updating') : $t('common.sending')) : (isEditMode ? $t('projects.updateAccess') : $t('projects.sendInvitation')) }}
          </AppButton>
        </PermissionGuard>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, reactive, watch, computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import AppModal from '@/components/base/Modal.vue';
import AppButton from '@/components/base/AppButton.vue';
import PermissionGuard from '@/components/common/PermissionGuard.vue';
import ProjectAccessService from '@/services/projectAccess.service';
import { usePermissions } from '@/composables/usePermissions';
import { PermissionType } from '@/services/permission.service';
import type { User } from '@/types';

// Props
interface Props {
  show: boolean;
  projectId: string | number;
  isEditMode?: boolean;
  accessId?: string | number | null;
  accessData?: Record<string, any>;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  isEditMode: false,
  accessId: null,
  accessData: () => ({})
});

// Expose commonly-used props to the template as refs (template unwraps refs)
const { show, isEditMode } = toRefs(props);

// Emits
interface Emits {
  (e: 'close'): void;
  (e: 'user-added', user: User): void;
  (e: 'invite-sent'): void;
  (e: 'access-updated'): void;
}

const emit = defineEmits<Emits>();

// Composables
const { t } = useI18n();
// Permissions composable must be called during setup (not later in callbacks)
const { hasPermissionForProject } = usePermissions();

// Reactive state
const firstInput = ref<HTMLInputElement | null>(null);
const isSubmitting = ref(false);
// Use centralized notifications
const { notifySuccess, notifyError } = useNotifications();
const invitationForm = reactive({
  clientName: '',
  email: '',
  accessRights: [] as string[]
});

const editForm = reactive({
  accessRights: [] as string[]
});

const validationErrors = reactive({
  clientName: '',
  email: '',
  accessRights: ''
});

// Icons for access rights - Material Design icons
const iconSet = {
  tasks: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19M17,17H7V7H17V17M15,15H9V9H15V15',
  phases: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19M17,17H7V7H17V17M15,15H9V9H15V15',
  photos: 'M13,9H18.5L13,3.5V9M6,2H14L20,8V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M6,20H18V12L14,16L12,14L6,20M8,9A2,2 0 0,0 6,11A2,2 0 0,0 8,13A2,2 0 0,0 10,11A2,2 0 0,0 8,9Z',
  documents: 'M13,9H18.5L13,3.5V9M6,2H14L20,8V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M11,4H6V20H18V11H11V4Z',
  budget: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
  intervenants: 'M16,13C15.71,13 15.38,13 15.03,13.05C16.19,13.89 17,15 17,16.5V19H23V16.5C23,14.17 18.33,13 16,13M8,13C5.67,13 1,14.17 1,16.5V19H15V16.5C15,14.17 10.33,13 8,13M8,11A3,3 0 0,0 11,8A3,3 0 0,0 8,5A3,3 0 0,0 5,8A3,3 0 0,0 8,11M16,11A3,3 0 0,0 19,8A3,3 0 0,0 16,5A3,3 0 0,0 13,8A3,3 0 0,0 16,11Z',
  notes: 'M14,10V4.5L19.5,10M5,3C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V9L15,3H5Z',
  design: 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z',
  viewReport: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19M7,17V7H9V17H7M11,17V11H13V17H11M15,17V14H17V17H15Z',
  audit: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19M7,17V7H9V17H7M11,17V11H13V17H11M15,17V14H17V17H15Z',
  viewIssues: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,6H13V13H11V6M11,16H13V18H11V16Z'
};

// Access rights options with descriptions
const accessOptions = [
  {
    value: 'MANAGE_TASKS',
    label: t('projects.manageTasks'),
    icon: iconSet.tasks
  },
  {
    value: 'PHASES',
    label: t('projects.managePhases'),
    icon: iconSet.phases
  },
  {
    value: 'PHOTOS',
    label: t('projects.photosAction'),
    icon: iconSet.photos
  },
  {
    value: 'DOCUMENTS',
    label: t('projects.documentsAction'),
    icon: iconSet.documents
  },
  {
    value: 'BUDGET',
    label: t('projects.budgetAction'),
    icon: iconSet.budget
  },
  {
    value: 'INTERVENANTS',
    label: t('projects.intervenantsAction'),
    icon: iconSet.intervenants
  },
  {
    value: 'NOTES',
    label: t('projects.notesAction'),
    icon: iconSet.notes
  },
  {
    value: 'DESIGN',
    label: t('projects.designAction'),
    icon: iconSet.design
  },
  {
    value: 'REPORTS',
    label: t('projects.viewReport'),
    icon: iconSet.viewReport
  },
  {
    value: 'ISSUES',
    label: t('projects.issuesAction'),
    icon: iconSet.viewIssues
  },
  {
    value: 'AUDIT',
    label: t('projects.auditTrail'),
    icon: iconSet.audit
  }
];

// Helper function to get access rights values.
// When editing, prefer the granted rights from props.accessData.accessRights (filtered to known options).
// Otherwise return all available option values (create mode default).
const getAllAccessRights = () => {
  // Determine which option values are allowed by the current user's permissions
  const allowedOptions = accessOptions.filter(option => {
    const perm = getPermissionForOption(option.value);
    // If there is no mapped permission, include by default
    if (!perm) return true;
    // Use composable's project-scoped permission check (computed ref) and read its value
    try {
      return !!hasPermissionForProject(perm as PermissionType, props.projectId).value;
    } catch (e) {
      console.warn('getAllAccessRights: permission check failed for', perm, e);
      return false;
    }
  }).map(o => o.value);

  // If editing and server provided granted rights, return their intersection with allowedOptions
  if (props.isEditMode && props.accessData && Array.isArray(props.accessData.accessRights)) {
    const granted: string[] = props.accessData.accessRights as string[]
    const allowedSet = new Set(allowedOptions)
    return granted.filter(r => allowedSet.has(r))
  }

  // Default (create mode): return allowed options
  return allowedOptions
}

// Map access option value to frontend permission name
const getPermissionForOption = (optionValue: string) => {
  switch (optionValue) {
    case 'MANAGE_TASKS':
      return 'ACCESS_TASKS';
    case 'PHOTOS':
      return 'ACCESS_PHOTO';
    case 'DOCUMENTS':
      return 'ACCESS_DOCUMENT';
    case 'BUDGET':
      return 'ACCESS_FINANCE';
    case 'INTERVENANTS':
      return 'ACCESS_INTERVENANTS';
    case 'NOTES':
      return 'NOTES';
    case 'DESIGN':
      return 'ACCESS_3D_DESIGN';
    case 'REPORTS':
      return 'VIEW_REPORTS';
    default:
      return null;
  }
};

// Computed property to get current access rights based on mode
const currentAccessRights = computed(() => {
  return props.isEditMode ? editForm.accessRights : invitationForm.accessRights;
});

// Close the dialog
const closeDialog = () => {
  resetForm();
  console.log('AccessInvite: closeDialog called');
  console.log('AccessInvite: Before emit, show prop is:', props.show);
  emit('update:show', false);
  console.log('AccessInvite: After emit update:show false');
};

// Reset form fields
const resetForm = () => {
  // Reset invitation form
  invitationForm.clientName = '';
  invitationForm.email = '';
  invitationForm.accessRights = [];
  
  // Reset edit form
  editForm.accessRights = [];
  
  // Clear validation errors
  validationErrors.clientName = '';
  validationErrors.email = '';
  validationErrors.accessRights = '';
  // Note: notifications are global, no local notification state to clear
};

// Toggle access right selection
const toggleAccessRight = (value: string) => {
  const targetForm = props.isEditMode ? editForm : invitationForm;
  const index = targetForm.accessRights.indexOf(value);
  if (index === -1) {
    targetForm.accessRights.push(value);
  } else {
    targetForm.accessRights.splice(index, 1);
  }
  validateOnInput('accessRights');
};

// Validate single field on input
const validateOnInput = (field: string) => {
  // When the user starts fixing issues we don't keep a local global error; centralized notifications are external

  switch (field) {
    case 'clientName': {
      validationErrors.clientName = '';
      const trimmedName = invitationForm.clientName.trim();
      
      if (trimmedName && trimmedName.length < 3) {
        validationErrors.clientName = t('projects.clientNameMinLength', 'Client name must be at least 3 characters');
      } else if (trimmedName && /[<>\\{}]/.test(trimmedName)) {
        validationErrors.clientName = t('projects.clientNameInvalidChars', 'Client name contains invalid characters');
      }
      break;
    }
      
    case 'email': {
      validationErrors.email = '';
      const trimmedEmail = invitationForm.email.trim();
      
      if (trimmedEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
          validationErrors.email = t('projects.invalidEmail', 'Please enter a valid email address');
        } else if (trimmedEmail.length > 100) {
          validationErrors.email = t('projects.emailTooLong', 'Email address is too long');
        }
      }
      break;
    }    case 'accessRights': {
      validationErrors.accessRights = '';
      // Get current form's access rights
      const currentAccessRights = props.isEditMode ? editForm.accessRights : invitationForm.accessRights;
      // Show validation message if access rights are selected then unselected
      if (currentAccessRights.length === 0) {
        validationErrors.accessRights = t('projects.accessRightsRequired', 'Please select at least one access right');
      }
      break;
    }
  }
};

// Validate form
const validateForm = () => {
  let isValid = true;
  
  // Reset validation errors
  validationErrors.clientName = '';
  validationErrors.email = '';
  validationErrors.accessRights = '';
  
  // Only validate client fields in create mode
  if (!props.isEditMode) {
    // Validate client name
    if (!invitationForm.clientName.trim()) {
      validationErrors.clientName = t('projects.clientNameRequired');
      isValid = false;
      firstInput.value?.focus();
    } else if (invitationForm.clientName.trim().length < 3) {
      validationErrors.clientName = t('projects.clientNameMinLength');
      isValid = false;
      firstInput.value?.focus();
    }
    
    // Validate email
    if (!invitationForm.email.trim()) {
      validationErrors.email = t('projects.emailRequired');
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(invitationForm.email.trim())) {
        validationErrors.email = t('projects.invalidEmail');
        isValid = false;
      }
    }
  }
  
  // Validate access rights - at least one must be selected (for both modes)
  const currentAccessRights = props.isEditMode ? editForm.accessRights : invitationForm.accessRights;
  if (currentAccessRights.length === 0) {
    validationErrors.accessRights = t('projects.accessRightsRequired');
    isValid = false;
  }
  
  return isValid;
};

const handleClick = () => {
  if (props.isEditMode) {
    submitUpdate();
  } else {
    submitInvitation();
  }
};

// Submit invitation
const submitInvitation = async () => {
  // Clear any previous validation state before submission
  
  // Reset validation errors
  validationErrors.clientName = '';
  validationErrors.email = '';
  validationErrors.accessRights = '';
  
  // Validate form
  if (!validateForm()) {
    return;
  }
  
  isSubmitting.value = true;
  
  try {
    await ProjectAccessService.inviteClient({
      projectId: props.projectId,
      clientName: invitationForm.clientName.trim(),
      email: invitationForm.email.trim(),
      accessRights: invitationForm.accessRights
    });
    
  // Show success notification via centralized system
  notifySuccess(t('projects.invitationSentSuccessfully', 'Invitation sent successfully!'));
    
    // Emit event to notify parent
    emit('invite-sent');

    // Reset form (but don't close dialog right away so user can see success message)
    invitationForm.clientName = '';
    invitationForm.email = '';
    invitationForm.accessRights = getAllAccessRights();
    
    // Close dialog after a short delay to allow user to see the success message
    setTimeout(() => {
      closeDialog();
    }, 2000);
  } catch (error: any) {
    console.error('Error sending invitation:', error);
    
    // Handle validation errors from backend
    if (error.response?.data) {
      const responseData = error.response.data;

      // Handle field-specific validation errors
      if (responseData.field) {
        // Map backend field names to frontend field names
        const fieldMap: Record<string, string> = {
          'clientName': 'clientName',
          'email': 'email', 
          'accessRights': 'accessRights',
          // Add other fields mappings as needed
        };
        
        const fieldName = fieldMap[responseData.field] || responseData.field;

        // Apply error to the specific field if we have a mapping for it
        if (fieldName in validationErrors) {
          (validationErrors as any)[fieldName] = responseData.message || t('errors.validationError');
          
          // For accessRights, handle specific validation failures
          if (fieldName === 'accessRights') {
            // Highlight the field to draw attention to it
            const accessRightsContainer = document.querySelector('.access-invite__access-rights-grid');
            if (accessRightsContainer) {
              accessRightsContainer.classList.add('access-invite__validation-shake');
              setTimeout(() => {
                accessRightsContainer.classList.remove('access-invite__validation-shake');
              }, 820);
            }
          }
        } else {
          // Unknown field, show via centralized notifications
          notifyError(`${responseData.field}: ${responseData.message}`);
        }
      }
      // Handle multiple validation errors (Spring Boot validation style)
      else if (responseData.errors && Array.isArray(responseData.errors)) {
        // Process all validation errors
        responseData.errors.forEach((err: any) => {
          if (err.field === 'email') {
            validationErrors.email = err.message || err.defaultMessage;
          } else if (err.field === 'clientName') {
            validationErrors.clientName = err.message || err.defaultMessage;
          } else if (err.field === 'accessRights') {
            validationErrors.accessRights = err.message || err.defaultMessage;
          } else {
            // For unknown fields, show each as a global notification
            notifyError(`${err.field}: ${err.message || err.defaultMessage}`);
          }
        });
      } 
      // Handle general error message
      else if (responseData.message) {
        notifyError(responseData.message);
      }
      // If we get here, we've got a response but can't parse it in any known format
      else {
        notifyError(t('errors.unexpectedServerResponse', 
          'Unexpected server response. Please try again later.'));
      }
    } else if (error.message) {
      // Network or other client-side error
      let errorMsg = t('errors.connectionError', 'Connection error. Please check your internet connection and try again.');
      
      // Provide more specific error messages based on common errors
      if (error.message.includes('timeout')) {
        errorMsg = t('errors.requestTimeout', 
          'The request timed out. The server might be busy, please try again later.');
      } else if (error.message.includes('Network Error')) {
        errorMsg = t('errors.networkError', 
          'Network connection error. Please check your internet connection and try again.');
      } else if (error.response?.status === 429) {
        errorMsg = t('errors.tooManyRequests', 
          'Too many requests. Please wait a moment before trying again.');
      }
      
      notifyError(errorMsg);
    } else {
      // Fallback for unknown errors
      notifyError(t('errors.unknownError', 
        'An unexpected error occurred. Please try again later.'));
    }  } finally {
    isSubmitting.value = false;
  }
};

// Submit access update for edit mode
const submitUpdate = async () => {
  // Clear any previous validation state before submission
  
  // Reset validation errors
  validationErrors.accessRights = '';
  
  // Validate form
  if (!validateForm()) {
    return;
  }
  
  isSubmitting.value = true;
  
  try {
    await ProjectAccessService.updateAccess(props.projectId, props.accessId, {
      clientName: props.accessData.clientName,
      accessRights: editForm.accessRights
    });
    
  // Show success notification via centralized system
  notifySuccess(t('projects.accessUpdatedSuccessfully', 'Access rights updated successfully!'));
    
    // Emit event to notify parent
    emit('access-updated');

    // Close dialog after a short delay to allow user to see the success message
    setTimeout(() => {
      closeDialog();
    }, 2000);
  } catch (error: any) {
    console.error('Error updating access:', error);
    
    // Handle validation errors from backend
    if (error.response?.data) {
      const responseData = error.response.data;

      // Handle field-specific validation errors
      if (responseData.field) {
        if (responseData.field === 'accessRights') {
          validationErrors.accessRights = responseData.message || t('errors.validationError');
          
          // Highlight the field to draw attention to it
          const accessRightsContainer = document.querySelector('.access-invite__access-rights-grid');
          if (accessRightsContainer) {
            accessRightsContainer.classList.add('access-invite__validation-shake');
            setTimeout(() => {
              accessRightsContainer.classList.remove('access-invite__validation-shake');
            }, 820);
          }
        } else {
          // Unknown field, show via centralized notifications
          notifyError(`${responseData.field}: ${responseData.message}`);
        }
      }
      // Handle multiple validation errors
      else if (responseData.errors && Array.isArray(responseData.errors)) {
        responseData.errors.forEach((err: any) => {
          if (err.field === 'accessRights') {
            validationErrors.accessRights = err.message || err.defaultMessage;
            } else {
            // For unknown fields, show each as a global notification
            notifyError(`${err.field}: ${err.message || err.defaultMessage}`);
          }
        });
      } 
      // Handle general error message
      else if (responseData.message) {
        notifyError(responseData.message);
      }
      // If we get here, we've got a response but can't parse it in any known format
      else {
        notifyError(t('errors.unexpectedServerResponse', 
          'Unexpected server response. Please try again later.'));
      }
    } else if (error.message) {
      // Network or other client-side error
      let errorMsg = t('errors.connectionError', 'Connection error. Please check your internet connection and try again.');
      
      // Provide more specific error messages based on common errors
      if (error.message.includes('timeout')) {
        errorMsg = t('errors.requestTimeout', 
          'The request timed out. The server might be busy, please try again later.');
      } else if (error.message.includes('Network Error')) {
        errorMsg = t('errors.networkError', 
          'Network connection error. Please check your internet connection and try again.');
      } else if (error.response?.status === 429) {
        errorMsg = t('errors.tooManyRequests', 
          'Too many requests. Please wait a moment before trying again.');
      }
      
      notifyError(errorMsg);
    } else {
      // Fallback for unknown errors
      notifyError(t('errors.unknownError', 
        'An unexpected error occurred. Please try again later.'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

// Set up focus trap when dialog is shown
watch(() => props.show, (newVal: boolean) => {
  if (newVal) {
    // Reset form and clear errors
    resetForm();
    
    // Initialize edit mode data if in edit mode
    if (props.isEditMode && props.accessData) {
      // Initialize edit form with current access rights
      editForm.accessRights = [...(props.accessData.accessRights || [])];
    } else {
      // Set all access rights as checked by default for create mode
      invitationForm.accessRights = getAllAccessRights();
    }
    
    // Focus first input after dialog is shown (only in create mode)
    setTimeout(() => {
      if (!props.isEditMode && firstInput.value) {
        firstInput.value.focus();
      }
    }, 100);
  } else {
    // When the dialog is hidden (parent closed it or v-model changed), ensure form state is cleared
    resetForm();
  }
});

// Watch for changes in validation errors to clear global error
watch([() => validationErrors.clientName, () => validationErrors.email, () => validationErrors.accessRights], 
  (newValues: string[], oldValues: string[]) => {
    // No local global error to clear; validation errors are kept local. Centralized notifications are independent.
  });
</script>

<style scoped>
.access-invite__content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.access-invite__form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.access-invite__client-info {
  background-color: var(--neutral-50, #f8f9fa);
  border: 1px solid var(--neutral-200, #e9ecef);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.access-invite__section-title {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.access-invite__info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.access-invite__info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.access-invite__info-item label {
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.access-invite__info-value {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
  padding: 0.75rem;
  background-color: white;
  border: 1px solid var(--neutral-300);
  border-radius: 4px;
  min-height: 1.5rem;
  display: flex;
  align-items: center;
}

.access-invite__form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.access-invite__form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.access-invite__form-group label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.access-invite__required {
  color: var(--error, #e53935);
  margin-left: 4px;
}

.access-invite__input-with-icon {
  position: relative;
}

.access-invite__input-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
}

.access-invite__form-group input,
.access-invite__form-group select {
  padding: 0.75rem;
  padding-left: 2.5rem;
  border: 1px solid var(--neutral-300);
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.2s ease;
  width: 100%;
}

.access-invite__form-group input.access-invite__has-error,
.access-invite__form-group select.access-invite__has-error {
  border-color: var(--error, #e53935);
  background-color: rgba(var(--error-rgb, 229, 57, 53), 0.05);
}

.access-invite__form-group input:focus,
.access-invite__form-group select:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb, 0, 123, 255), 0.15);
}

.access-invite__form-hint {
  color: var(--text-secondary);
  font-size: 0.75rem;
}

.access-invite__error-message {
  color: var(--error, #e53935);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
}

.access-invite__error-message::before {
  content: "!";
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: var(--error, #e53935);
  color: white;
  border-radius: 50%;
  font-size: 12px;
  margin-right: 6px;
  font-weight: bold;
}

.access-invite__access-rights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}

.access-invite__access-option {
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: 100%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.access-invite__access-option:hover {
  background-color: var(--neutral-50, #f8f9fa);
  transform: translateY(-1px);
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.08);
}

.access-invite__access-option-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid var(--neutral-400);
  border-radius: 4px;
  transition: all 0.2s ease;
  position: relative;
  flex-shrink: 0;
}

.access-invite__access-option-checkbox input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.access-invite__access-option-checkbox::before {
  content: '';
  display: inline-block;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.access-invite__access-option.access-invite__selected {
  border-color: var(--primary);
  background-color: rgba(var(--primary-rgb, 0, 123, 255), 0.05);
  box-shadow: 0 0 0 1px var(--primary);
}

.access-invite__access-option.access-invite__selected .access-invite__access-option-checkbox {
  border-color: var(--primary);
  background-color: var(--primary);
}

.access-invite__access-option.access-invite__selected .access-invite__access-option-checkbox::before {
  content: '✓';
  color: white;
  font-size: 14px;
  line-height: 1;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.access-invite__access-option-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.access-invite__access-option-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.access-invite__access-option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
}

.access-invite__access-option.access-invite__selected .access-invite__access-option-icon {
  color: var(--primary);
}

.access-invite__access-option-title {
  font-weight: 600;
  color: var(--text-primary);
}

.access-invite__dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.access-invite__note-container {
  margin-bottom: 1rem;
}

.access-invite__info-note {
  background-color: rgba(var(--primary-rgb, 0, 123, 255), 0.1);
  border-left: 3px solid var(--primary);
  padding: 0.75rem 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  border-radius: 4px;
}

.access-invite__info-note p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.access-invite__info-icon {
  color: var(--primary);
  flex-shrink: 0;
  margin-top: 2px;
}

/* Global notifications */
.access-invite__global-error-notification,
.access-invite__global-success-notification {
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  animation: slideDown 0.3s ease;
}

.access-invite__global-error-notification {
  background-color: rgba(var(--error-rgb, 229, 57, 53), 0.1);
  border-left: 3px solid var(--error, #e53935);
}

.access-invite__global-success-notification {
  background-color: rgba(var(--success-rgb, 67, 160, 71), 0.1);
  border-left: 3px solid var(--success, #43a047);
}

.access-invite__notification-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex: 1;
}

.access-invite__notification-icon {
  flex-shrink: 0;
}

.access-invite__error-icon {
  color: var(--error, #e53935);
}

.access-invite__success-icon {
  color: var(--success, #43a047);
}

.access-invite__notification-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.access-invite__notification-close:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes access-invite__validation-shake {
  0%, 100% {
    transform: translateX(0);
  }
  15%, 45%, 75% {
    transform: translateX(-6px);
    box-shadow: 0 0 8px rgba(var(--error-rgb, 229, 57, 53), 0.4);
  }
  30%, 60%, 90% {
    transform: translateX(6px);
    box-shadow: 0 0 8px rgba(var(--error-rgb, 229, 57, 53), 0.4);
  }
}

.access-invite__validation-shake {
  animation: access-invite__validation-shake 0.8s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  border-color: var(--error, #e53935) !important;
  background-color: rgba(var(--error-rgb, 229, 57, 53), 0.05) !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .access-invite__form-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .access-invite__access-rights-grid {
    grid-template-columns: 1fr;
  }
  
  .access-invite__info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .access-invite__dialog-actions {
    flex-direction: column-reverse;
  }
  
  .access-invite__dialog-actions .btn {
    width: 100%;
  }
}
</style>
